import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { grey } from "@mui/material/colors";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid/Grid";
import IconButton from "@mui/material/IconButton/IconButton";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField/TextField";
import Typography from "@mui/material/Typography/Typography";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import { rollOverSchedulesCancel } from "../../store/modules/rollOver/rollOverActions";
import { RollOverScheduleHttpResponse } from "../../store/parentApi";
import { RootState } from "../../store/store";
import { getStyleColor } from "../../utils/helpers";
import { ButtonPrimary } from "../Common/Buttons/Buttons";
import { AnimatedDialog } from "../Common/Dialogs/AnimatedDialog";
import Spacer from "../Common/Spacers/Spacers";

interface IProps {
  schedules: RollOverScheduleHttpResponse[];
  handleClose: (showSaved: boolean) => void;
  open: boolean;
  year: number;
}

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  columnText: {
    fontSize: 16,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  title: {
    marginTop: theme.spacing(2),
  },
  select: {
    "&:focus": {
      backgroundColor: "white",
      //borderColor: "brown",
    },
  },

  formControl: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },

  disabledButton: {
    backgroundColor: grey[500] + "!important",
  },
}));

export function CancelRolloverDialog(props: IProps) {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { schedules, year, handleClose, open } = props;
  const childId = schedules[0].childId;
  const rollIds = schedules.map((s) => s.rollId);

  const [cancellationReason, setCancellationReason] = React.useState<number | null>(null);
  const [otherReason, setOtherreason] = React.useState<string | undefined>();
  const customerAccountId = useSelector((state: RootState) => state.customer.customer?.customerAccountId);
  const rollOverScheduleCancellationReasons = useSelector(
    (state: RootState) => state.commonData.data?.rollOverScheduleCancellationReasons
  );
  const isSaving = useSelector((state: RootState) => state.rollOverReducer.isSaving);

  function cancelRollOverSchedules() {
    if (cancellationReason !== null) {
      rollOverSchedulesCancel(dispatch, customerAccountId!, childId, rollIds, cancellationReason, () => {
        handleClose(true);
      });
    }
  }

  return (
    <AnimatedDialog open={open} onClose={() => handleClose(false)} sx={{ borderRadius: 33, p: 0 }}>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Box>
        <DialogTitle id="alert-dialog-title">
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", alignContent: "center" }}>
            <ErrorIcon
              sx={{
                mx: "auto",
                background: "black",
                color: getStyleColor("--youroshc-color-primary"),
                borderRadius: "50%",
                width: { xs: 25, md: 32 },
                height: { xs: 25, md: 32 },
              }}
            ></ErrorIcon>

            <Typography
              variant="h6"
              align="center"
              sx={{ fontWeight: "bold", py: { xs: 0, md: 1 }, fontSize: { xs: 15, md: 20 } }}
            >
              You're about to opt out of care next year. Can you tell us why?
            </Typography>
          </Box>

          <IconButton aria-label="close" className={classes.closeButton} onClick={() => handleClose(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={0} justifyContent="center" alignItems="center" direction="column">
            <Spacer margin={5} />

            <FormControl required variant="outlined" className={classes.formControl} fullWidth>
              <Select
                className={classes.select}
                native
                displayEmpty={true}
                value={cancellationReason}
                defaultValue={null}
                fullWidth
                onChange={(evt) => {
                  var value = evt.target.value;
                  if (value !== "") {
                    var id = parseInt(evt.target.value as string);
                    setCancellationReason(id);
                  } else {
                    setCancellationReason(null);
                  }
                }}
              >
                <option value="">Please select a reason</option>
                {rollOverScheduleCancellationReasons?.map((r) => (
                  <option key={r.scheduleCancellationReasonId} value={r.scheduleCancellationReasonId}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            {cancellationReason === 0 && (
              <FormControl required fullWidth>
                <TextField
                  label="Other reason"
                  placeholder={"Reason for cancellation"}
                  inputProps={{ maxLength: 80 }}
                  value={otherReason ?? null}
                  onChange={(e) => setOtherreason(e.target.value)}
                  variant="outlined"
                  required
                ></TextField>
              </FormControl>
            )}

            <Spacer margin={10} />
            <Grid container justifyContent="center">
              <ButtonPrimary
                style={{
                  backgroundColor: getStyleColor("--youroshc-color-primary"),
                  color: "black",
                  fontWeight: "bold",
                }}
                onClick={() => {
                  cancelRollOverSchedules();
                }}
                disabled={cancellationReason == null}
              >
                I don't want my {year} Schedule
              </ButtonPrimary>
            </Grid>
          </Grid>
        </DialogContent>
      </Box>
    </AnimatedDialog>
  );
}
